// ============================================================
// Risma platform API client.
//
// Usage:
//   const client = createClient({ baseUrl: 'http://localhost:3000' });
//   const { user, tenant } = (await client.me()).data;
//   const { data: reservations } = await client.reservations();
//
// All methods send `credentials: 'include'` so the session cookie set
// by /v1/auth/verify travels with the request. The auth flow itself
// (POST /v1/auth/magic-link, GET /v1/auth/verify) is exposed separately
// for completeness.
// ============================================================
import type {
  WireMe,
  WireZonesPayload,
  WireZone,
  WireTable,
  WireCustomer,
  WireReservation,
  WireNotification,
  FeatureSpec,
  SettingsPatchInput,
  WireSiteContent,
  SiteContent,
} from './types';

/** Body of POST /v1/reservations. */
export interface ReservationCreateInput {
  name: string;
  phone?: string | null;
  covers: number;
  /** ISO 8601. */
  startsAt: string;
  service: string;
  tableId?: string | null;
  customerId?: string | null;
  source: 'web' | 'guest' | 'phone' | 'walkin';
  status?: 'new' | 'conf' | 'seated' | 'done' | 'noshow' | 'cancel';
  durationMinutes?: number;
  tags?: ('vip' | 'regular' | 'allergy')[];
  noteIt?: string | null;
  noteEn?: string | null;
}

/** Body of PATCH /v1/reservations/:id. All fields optional; only the
 *  ones present in the body are updated. */
export interface ReservationPatchInput {
  status?: 'new' | 'conf' | 'seated' | 'done' | 'noshow' | 'cancel';
  name?: string;
  phone?: string | null;
  covers?: number;
  /** ISO 8601 timestamp. */
  startsAt?: string;
  service?: string;
  /** UUID of the table to assign, or null to unassign. */
  tableId?: string | null;
  durationMinutes?: number;
  tags?: ('vip' | 'regular' | 'allergy')[];
  noteIt?: string | null;
  noteEn?: string | null;
}

/** Body of PATCH /v1/customers/:id. */
export interface CustomerPatchInput {
  name?: string;
  phone?: string | null;
  email?: string | null;
  favouriteDish?: string | null;
  noteIt?: string | null;
  noteEn?: string | null;
  tags?: ('vip' | 'regular' | 'allergy')[];
}

/** Body of PATCH /v1/notifications/:id. */
export interface NotificationPatchInput {
  read: boolean;
}

export type { WireMe, WireZonesPayload, WireCustomer, WireReservation, WireNotification } from './types';
export type {
  Lang,
  Localized,
  WireZone,
  WireTable,
  FeatureKey,
  FeatureTier,
  FeatureSpec,
  TenantFeatures,
  WireTenantSettings,
  SettingsPatchInput,
} from './types';
export type {
  WireSiteContent,
  SiteContent,
  SiteImage,
  SiteTimeRange,
  SiteWeekday,
  SiteDayHours,
  SiteMenuTag,
  SiteMenuItem,
  SiteMenuCategory,
  SiteMenuList,
} from './types';

interface ClientOptions {
  /** API origin, e.g. http://localhost:3000 or https://api.rismastudio.it */
  baseUrl: string;
  /**
   * Optional fetch override — useful for testing or for server-side rendering
   * where `globalThis.fetch` isn't ideal. Defaults to the global fetch.
   */
  fetch?: typeof globalThis.fetch;
}

interface Envelope<T> {
  data: T;
}

/** Thrown when the API responds with a non-2xx status. */
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message?: string,
  ) {
    super(message ?? code);
    this.name = 'ApiError';
  }
}

/** Body of POST /v1/zones (crea una zona della sala). */
export interface ZoneCreateInput { nameIt: string; nameEn?: string; bookableOnline?: boolean }
export interface ZonePatchInput { nameIt?: string; nameEn?: string; bookableOnline?: boolean; sortOrder?: number }
/** Body of POST /v1/zones/tables (crea un tavolo in una zona). */
export interface TableCreateInput { zoneId: string; label: string; seats: number }
export interface TablePatchInput { label?: string; seats?: number; sortOrder?: number; zoneId?: string }

export interface RismaClient {
  /** Current user + tenant. 401 if unauthenticated. */
  me(): Promise<Envelope<WireMe>>;

  /** All reservations for the current tenant, ordered by startsAt asc. */
  reservations(): Promise<Envelope<WireReservation[]>>;

  /** All customers, ordered by visits desc. */
  customers(): Promise<Envelope<WireCustomer[]>>;

  /** Zones + tables in one shot. */
  zones(): Promise<Envelope<WireZonesPayload>>;

  /** Notifications, newest first. */
  notifications(): Promise<Envelope<WireNotification[]>>;

  /** Feature catalog — all known features, their tier, labels, descriptions. */
  features(): Promise<Envelope<FeatureSpec[]>>;

  // ── mutations ────────────────────────────────────────────────
  /** Create a reservation. Body fields match POST /v1/reservations. */
  createReservation(input: ReservationCreateInput): Promise<Envelope<WireReservation>>;

  /** Patch a reservation (status changes are the main use today). */
  updateReservation(id: string, patch: ReservationPatchInput): Promise<Envelope<WireReservation>>;

  /** Patch a customer (tags edit is the main use today). */
  updateCustomer(id: string, patch: CustomerPatchInput): Promise<Envelope<WireCustomer>>;

  /** Mark every unread notification as read. */
  markAllNotificationsRead(): Promise<void>;

  /** Flip a single notification's read flag. */
  updateNotification(id: string, patch: NotificationPatchInput): Promise<Envelope<WireNotification>>;

  /** Patch the current tenant's settings (auto-confirm threshold, etc.). */
  updateSettings(patch: SettingsPatchInput): Promise<Envelope<WireMe>>;

  // ── sala: zone + tavoli (#39) ────────────────────────────────
  createZone(input: ZoneCreateInput): Promise<Envelope<WireZone>>;
  updateZone(id: string, patch: ZonePatchInput): Promise<Envelope<WireZone>>;
  deleteZone(id: string): Promise<void>;
  createTable(input: TableCreateInput): Promise<Envelope<WireTable>>;
  updateTable(id: string, patch: TablePatchInput): Promise<Envelope<WireTable>>;
  deleteTable(id: string): Promise<void>;

  // ── website editor ───────────────────────────────────────────
  /** Current website content (draft + published) for the tenant. */
  getSite(): Promise<Envelope<WireSiteContent>>;
  /** Save the working draft (validated server-side). */
  saveSiteDraft(content: SiteContent): Promise<Envelope<{ ok: boolean; updatedAt: string }>>;
  /** Save ONLY the brand colours — independent of the rest of the site, so an
   *  incomplete draft can never block a colour change. Syncs the portal accent
   *  and rebuilds the live site if it was already published. */
  saveSiteTheme(theme: NonNullable<SiteContent['theme']>): Promise<Envelope<{ ok: boolean; updatedAt: string; rebuilt: boolean }>>;
  /** Promote the draft to published (triggers a rebuild in Phase 3). */
  publishSite(): Promise<Envelope<{ ok: boolean; publishedAt: string }>>;
  /** Upload a photo (image blob); resolves to its public URL. */
  uploadSiteImage(file: Blob): Promise<Envelope<{ url: string }>>;

  // ── auth ─────────────────────────────────────────────────────
  /** Request a magic link for an email + tenant slug. Always 204. */
  requestMagicLink(input: { email: string; tenantSlug: string }): Promise<void>;
  /** Revoke the current session, clear the cookie. */
  logout(): Promise<void>;
  /** Consume a staff impersonation token → issues an (impersonated) owner session. */
  consumeImpersonation(token: string): Promise<void>;
}

export function createClient(opts: ClientOptions): RismaClient {
  const f = opts.fetch ?? globalThis.fetch.bind(globalThis);
  const base = opts.baseUrl.replace(/\/$/, '');

  async function call<T>(
    path: string,
    init: RequestInit = {},
  ): Promise<T> {
    const res = await f(`${base}${path}`, {
      ...init,
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        ...(init.body ? { 'Content-Type': 'application/json' } : {}),
        ...(init.headers ?? {}),
      },
    });

    if (!res.ok) {
      let body: { error?: string } = {};
      try {
        body = (await res.json()) as { error?: string };
      } catch {
        // non-JSON error body; ignore
      }
      throw new ApiError(res.status, body.error ?? 'http_error', `HTTP ${res.status}`);
    }

    // 204 No Content
    if (res.status === 204) return undefined as T;

    return (await res.json()) as T;
  }

  return {
    me: () => call<Envelope<WireMe>>('/v1/me'),
    reservations: () => call<Envelope<WireReservation[]>>('/v1/reservations'),
    customers: () => call<Envelope<WireCustomer[]>>('/v1/customers'),
    zones: () => call<Envelope<WireZonesPayload>>('/v1/zones'),
    notifications: () => call<Envelope<WireNotification[]>>('/v1/notifications'),
    features: () => call<Envelope<FeatureSpec[]>>('/v1/features'),

    // ── mutations ──────────────────────────────────────────
    createReservation: (input) =>
      call<Envelope<WireReservation>>('/v1/reservations', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    updateReservation: (id, patch) =>
      call<Envelope<WireReservation>>(`/v1/reservations/${encodeURIComponent(id)}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      }),
    updateCustomer: (id, patch) =>
      call<Envelope<WireCustomer>>(`/v1/customers/${encodeURIComponent(id)}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      }),
    markAllNotificationsRead: async () => {
      await call<void>('/v1/notifications/read-all', { method: 'POST' });
    },
    updateNotification: (id, patch) =>
      call<Envelope<WireNotification>>(`/v1/notifications/${encodeURIComponent(id)}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      }),
    updateSettings: (patch) =>
      call<Envelope<WireMe>>('/v1/me/settings', {
        method: 'PATCH',
        body: JSON.stringify(patch),
      }),

    // ── sala: zone + tavoli ──────────────────────────────────
    createZone: (input) =>
      call<Envelope<WireZone>>('/v1/zones', { method: 'POST', body: JSON.stringify(input) }),
    updateZone: (id, patch) =>
      call<Envelope<WireZone>>(`/v1/zones/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(patch) }),
    deleteZone: (id) =>
      call<void>(`/v1/zones/${encodeURIComponent(id)}`, { method: 'DELETE' }),
    createTable: (input) =>
      call<Envelope<WireTable>>('/v1/zones/tables', { method: 'POST', body: JSON.stringify(input) }),
    updateTable: (id, patch) =>
      call<Envelope<WireTable>>(`/v1/zones/tables/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(patch) }),
    deleteTable: (id) =>
      call<void>(`/v1/zones/tables/${encodeURIComponent(id)}`, { method: 'DELETE' }),

    getSite: () => call<Envelope<WireSiteContent>>('/v1/site'),
    saveSiteDraft: (content) =>
      call<Envelope<{ ok: boolean; updatedAt: string }>>('/v1/site/draft', {
        method: 'PUT',
        body: JSON.stringify(content),
      }),
    saveSiteTheme: (theme) =>
      call<Envelope<{ ok: boolean; updatedAt: string; rebuilt: boolean }>>('/v1/site/theme', {
        method: 'POST',
        body: JSON.stringify(theme),
      }),
    publishSite: () =>
      call<Envelope<{ ok: boolean; publishedAt: string }>>('/v1/site/publish', {
        method: 'POST',
      }),
    uploadSiteImage: (file) =>
      // Raw image body; Content-Type names the format (overrides the JSON
      // default in call()). The server reads the bytes directly.
      call<Envelope<{ url: string }>>('/v1/site/upload', {
        method: 'POST',
        body: file,
        headers: { 'Content-Type': file.type || 'application/octet-stream' },
      }),

    requestMagicLink: async ({ email, tenantSlug }) => {
      await call<void>('/v1/auth/magic-link', {
        method: 'POST',
        body: JSON.stringify({ email, tenantSlug }),
      });
    },
    logout: async () => {
      await call<void>('/v1/auth/logout', { method: 'POST' });
    },
    consumeImpersonation: async (token) => {
      await call<void>('/v1/auth/impersonate/consume', {
        method: 'POST',
        body: JSON.stringify({ token }),
      });
    },
  };
}
